import React from 'react';
import {
  Card,
  Text,
  Badge,
} from '@fluentui/react-components';
import { mockSupplyChainExceptions, mockSCMetrics } from '../../data/mockSupplyChainData';
import styles from './SupplyChainCommandCenter.module.css';

export interface SupplyChainCommandCenterProps {
  onCaseSelect?: (caseId: string) => void;
}

/**
 * Format SLA exposure as a compact currency string.
 * e.g. 820000 → "$820K", 410000 → "$410K"
 */
function formatExposure(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    const thousands = amount / 1_000;
    return `$${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  return `$${amount}`;
}

/**
 * Get badge color based on severity level.
 */
function getSeverityColor(severity: string): 'danger' | 'warning' | 'important' | 'informative' {
  switch (severity) {
    case 'Critical':
      return 'danger';
    case 'High':
      return 'warning';
    case 'Medium':
      return 'important';
    default:
      return 'informative';
  }
}

const SupplyChainCommandCenter: React.FC<SupplyChainCommandCenterProps> = ({
  onCaseSelect,
}) => {
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Text className={styles.title} as="h1">
          Supply Chain Command Center
        </Text>
        <Text className={styles.subtitle} as="p">
          Monitor supplier risk, delivery exceptions, SLA exposure, and fulfillment commitments
          across planning, procurement, logistics, and operations.
        </Text>
      </div>

      {/* Metrics Grid */}
      <div className={styles.metricsGrid}>
        {mockSCMetrics.map((metric) => (
          <Card key={metric.label} className={styles.metricCard}>
            <Text className={styles.metricValue}>{metric.value}</Text>
            <Text className={styles.metricLabel}>{metric.label}</Text>
          </Card>
        ))}
      </div>

      {/* Priority Supply Chain Exceptions Table */}
      <Card className={styles.tableSection}>
        <Text className={styles.tableTitle}>Priority Supply Chain Exceptions</Text>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Case ID</th>
                <th>Supplier/Lane</th>
                <th>Exception Type</th>
                <th>Exposure</th>
                <th>Due In</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {mockSupplyChainExceptions.map((exception) => (
                <tr key={exception.caseId}>
                  <td>
                    <span
                      className={styles.caseIdLink}
                      onClick={() => onCaseSelect?.(exception.caseId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onCaseSelect?.(exception.caseId);
                        }
                      }}
                    >
                      {exception.caseId}
                    </span>
                  </td>
                  <td>{exception.customer}</td>
                  <td>{exception.exceptionType}</td>
                  <td>
                    <span className={styles.exposureValue}>
                      {formatExposure(exception.slaExposure)}
                    </span>
                  </td>
                  <td>{exception.daysOverdue > 0 ? `${exception.daysOverdue}d overdue` : 'Not yet due'}</td>
                  <td>
                    <Badge
                      appearance="filled"
                      color={getSeverityColor(exception.severity)}
                    >
                      {exception.recommendation}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default SupplyChainCommandCenter;
